import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { notification } from "antd";
import CleanButton from "../../common/components/CleanButton";
import { formattedDate, QueryList, resetFilters } from "../../common/constants/Helpers";
import { Tooltips } from "../../common/constants/Tooltips";
import { LookupRequest } from "../../models/lookup-request";
import { buildLookupRequest, buildSummaryRequest, session } from "../../state/connect-api";
import { useGetCaseSummaryMutation, useLookupCaseMutation } from "../../state/globalApi";
import { resetCaseMaster, setActiveCaseNumber, setQueryResult, updateCaseMaster, updateCasePayment } from "../../state/globalStore";
import ColumnSearch from "../../common/components/ColumnSearch";
import { usePaymentAPIMutation } from "../../state/query-api";
import { useLocation } from "react-router";
import { RootState } from "../../store";
import getConfigs from "../../GetConfigs";

interface StateType {
  state: {
    fromPage?: string;
  };
  key: string;
  pathname: string;
  search: string;
  hash: string;
}

const useTrafficCriminalLookup = () => {
  const [condition, setCondition] = useState(null);
  const [search, setSearch] = useState(null);
  const [data, setData] = useState([]);
  const [visibility, setVisibility] = useState<boolean>(false);
  const history = useNavigate();
  const dispatch = useDispatch();
  const [lookupCase, { isLoading }] = useLookupCaseMutation();
  const [getCaseSummary] = useGetCaseSummaryMutation();
  const [returnStatus, setReturnStatus] = useState<boolean>(false);
  const [getPaymentData, { isLoading: paymentLoading, data: paymentData }] = usePaymentAPIMutation();
  const location = useLocation();
  const { queryResult } = useSelector((state: RootState) => state.global);
  const loginType = localStorage.getItem("LOGINTYPE");
  const configurations = getConfigs([
    "captchaEnabled"
  ]);
  const searchCaptcha = useRef(null);

  const handleSearch = async () => {
    if (!condition || !search) {
      notification.error({ message: "You must select a search condition and provide a value to search for." });
      return;
    }

    const lookupRequest: LookupRequest = buildLookupRequest(condition, search);
    const results = await lookupCase(lookupRequest).unwrap();
    if (condition === "CASE") {
      await handleSelectCase(search);
    }
    localStorage.setItem("condition", condition.toString());
    localStorage.setItem("search", search.toString());
    dispatch(setQueryResult({ LIST: results?.LIST }));
    setData(results?.LIST);
  };

  const handleSelectCase = async (caseNumber: string) => {
    dispatch(setActiveCaseNumber(caseNumber));
    const request: LookupRequest = buildSummaryRequest(caseNumber);
    const results = await getCaseSummary(request).unwrap();
    dispatch(updateCaseMaster({ summary: results }));
    history("/lookup/detail", { replace: true, state: { fromPage: "TCL" } });
    return;
  };

  useEffect(() => {
    dispatch(resetCaseMaster());
  }, [dispatch]);

  const columns = [
    { key: "case", dataIndex: "CASE", title: "Case", ...ColumnSearch("CASE", "Case", resetFilters) },
    { key: "name", dataIndex: "NAME", title: "Name", ...ColumnSearch("NAME", "Name", resetFilters) },
    { key: "dob", dataIndex: "DATE_OF_BIRTH", title: "DOB", ...ColumnSearch("DATE_OF_BIRTH", "DOB", resetFilters) },
    { key: "offense", dataIndex: "OFFENSE", title: "Offense", ...ColumnSearch("OFFENSE", "Offense", resetFilters) },
    { key: "date", dataIndex: "VIOLATION_DATE", title: "Date", render: (date) => formattedDate(date) },
    { key: "status", dataIndex: "STATDEF", title: "Status" },
    { key: "actions", dataIndex: "CASE", title: "Actions", render: (caseNumber: string) => <CleanButton icon="eye" type="link" tooltip={Tooltips.View} onClick={() => handleSelectCase(caseNumber)} /> }
  ];

  const fixedColumns = [
    { key: "field", dataIndex: "FIELD", title: "Field" },
    { key: "description", dataIndex: "DESCRIPTION", title: "Description" }
  ];

  const fixedData = [
    { FIELD: "Name", DESCRIPTION: "Last name,First name or Last name only. Comma between names required. Space after comma optional. (Example: Smith,Jonathan or Smith)" },
    {
      FIELD: "Soundex",
      DESCRIPTION:
        "Sound Index Search (How it sounds, rather than spelled) Last name (or Last name,First name - comma required). (Example: Jacksen or Smyth,James) NOTE! These searches can take an extended period of time to complete!"
    },
    { FIELD: "Case", DESCRIPTION: "Case Number. Format - 2 Digit Year, Case Type, Number (Example: 12TRC12345)." },
    { FIELD: "DOB", DESCRIPTION: "Date of Birth in the format MM/DD/YYYY (Example: 05/07/1968)." },
    { FIELD: "Ticket", DESCRIPTION: "Traffic Ticket number." },
    { FIELD: "License Plate", DESCRIPTION: "Vehicle License Plate Number." },
    { FIELD: "Incident", DESCRIPTION: "Incident Number." },
    { FIELD: loginType === "A" ? "SSN" : null, DESCRIPTION: loginType === "A" ? "Social Security Number in the format NNN-NN-NNNN. (Example: 123-45-6789)." : null },
    { FIELD: loginType === "A" ? "FID/ALT ID" : null, DESCRIPTION: loginType === "A" ? "Federal or Alternate ID." : null }
  ];

  const handleReturnToResult = async () => {
    const stateObject: StateType = { ...location };
    const conditionValue = localStorage.getItem("condition");
    const searchValue = localStorage.getItem("search");
    let route = "";
    let dataToPass = {};
    switch (String(stateObject.state.fromPage)) {
      case "TCL":
        route = "/lookup";
        dataToPass = { returnData: queryResult?.LIST, condition: conditionValue, search: searchValue, returnStatus: true, fromPage: stateObject.state.fromPage };
        break;
      case "AW":
        route = "/warrants";
        dataToPass = { returnData: queryResult?.LIST, condition: conditionValue, search: searchValue, returnStatus: true, fromPage: stateObject.state.fromPage };
        break;
      case String(QueryList.TRCRDaily):
        route = "/tr-cr-query/daily-query/list";
        dataToPass = stateObject.state.fromPage;
        break;
      case String(QueryList.TRCRArraignment):
        route = "/tr-cr-query/arraignment-query/list";
        dataToPass = stateObject.state.fromPage;
        break;
      case String(QueryList.TRCRAttorney):
        route = "/tr-cr-query/attorney-query/list";
        dataToPass = stateObject.state.fromPage;
        break;
      case String(QueryList.TRCRAgency):
        route = "/tr-cr-query/agency-query/list";
        dataToPass = stateObject.state.fromPage;
        break;
    }
    history(route, { replace: true, state: dataToPass });
  };

  const handleNewQuery = async () => {
    const stateObject: StateType = { ...location };
    let route = "/lookup";
    dispatch(setActiveCaseNumber(null));
    dispatch(setQueryResult(null));
    localStorage.removeItem("condition");
    localStorage.removeItem("search");
    if (stateObject.state.fromPage === "AW") {
      route = "/warrants";
    }
    history(route);
  };

  const handleFetchCaseBalance = async (caseNumber) => {
    const paymentLookupRequest: LookupRequest = buildLookupRequest(caseNumber, session, caseNumber, " ", "N");
    //N stands for false which means user deined for TSP Program
    //It needs to be dynamic instead of static
    //@AP: TODO
    const stateObject: StateType = { ...location };
    const results = await getPaymentData(paymentLookupRequest).unwrap();
    dispatch(updateCasePayment({ LIST: results?.LIST, MAIN: results?.MAIN }));
    let totalAmount = 0;
    results?.LIST.map((eachPayment) => (totalAmount += Number(eachPayment.TOTAL_AMOUNT)));
    if (totalAmount) {
      history("/detail/pay", { replace: true, state: { fromPage: stateObject.state.fromPage } });
    } else {
      notification.warn({ message: results?.MAIN.length && results?.MAIN[0].PAY_MESSAGE ? results?.MAIN[0].PAY_MESSAGE : "Cannot process the payment" });
    }
  };

  const handleViewPDF = () => {
    setVisibility(true);
  };

  const handleEFile = () => {
    let route = "/EFile";
    history(route);
  }

  const validateSearchCaptcha = () => {
    if (configurations.captchaEnabled == "true") {
      searchCaptcha.current.execute();
    }
    else {
      handleSearch();
    }
  }

  return {
    data,
    condition,
    setCondition,
    search,
    setSearch,
    handleSearch,
    columns,
    loading: isLoading,
    handleReturnToResult,
    setData,
    handleNewQuery,
    returnStatus,
    setReturnStatus,
    fixedColumns,
    fixedData,
    handleViewPDF,
    visibility,
    setVisibility,
    handleFetchCaseBalance,
    paymentLoading,
    paymentData,
    handleEFile,
    searchCaptcha,
    validateSearchCaptcha
  };
};

export default useTrafficCriminalLookup;
